import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../../hooks/ApiProvider/ApiProvider';
import { useMembership } from '../../../hooks/MembershipProvider/MembershipProvider';
import styles from './CouponCreate.module.css';

interface ProductInfo {
  id: string;
  name: string;
  price: number;
  description?: string;
}

interface CouponCreateFormData {
  product_id: string;
  count: number;
  valid_from: string;
  valid_until: string;
}

const CouponCreate: React.FC = () => {
  const { getProductsForCoupon, createDummyOrderAndIssueGiftCoupons } = useApi();
  const { user, hasAdminPermission, hasSystemPermission } = useMembership();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductInfo[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductInfo | null>(null);

  // 초기 날짜 설정: 발행일(오늘), 만료일(3개월 후)
  const today = new Date();
  const threeMonthsLater = new Date();
  threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
  
  const [formData, setFormData] = useState<CouponCreateFormData>({
    product_id: '',
    count: 1,
    valid_from: today.toISOString().split('T')[0],
    valid_until: threeMonthsLater.toISOString().split('T')[0]
  });

  // 상품 목록 조회
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getProductsForCoupon();
      // 서버에서 직접 플랜 데이터를 반환하므로 배열로 변환
      const productsArray = Object.values(response).map((plan: any) => ({
        id: plan.id,
        name: plan.name,
        price: plan.price,
        description: plan.description
      }));
      setProducts(productsArray);
    } catch (err) {
      console.error('상품 목록 조회 실패:', err);
      setError(err instanceof Error ? err.message : '상품 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // 폼 입력 핸들러
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'count' ? Number(value) : value
    }));

    // 상품 선택 시 상품 정보 업데이트
    if (name === 'product_id') {
      const product = products.find(p => p.id === value);
      setSelectedProduct(product || null);
    }
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // 폼 검증
      if (!formData.product_id || formData.product_id.trim() === '') {
        throw new Error('상품을 선택해주세요.');
      }
      if (formData.count <= 0 || formData.count > 100) {
        throw new Error('수량은 1개 이상 100개 이하여야 합니다.');
      }

      // 날짜 유효성 검사
      const validFrom = new Date(formData.valid_from);
      const validUntil = new Date(formData.valid_until);
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      if (validFrom < currentDate) {
        throw new Error('발행일은 현재 날짜 이전일 수 없습니다.');
      }
      if (validUntil <= validFrom) {
        throw new Error('만료일은 발행일보다 늦어야 합니다.');
      }
      if (validUntil <= currentDate) {
        throw new Error('만료일은 현재 날짜 이후여야 합니다.');
      }

      // API 호출 - ISO 8601 형식으로 변환
      const requestData = {
        product_id: formData.product_id.trim(),
        count: Number(formData.count),
        valid_from: `${formData.valid_from}T00:00:00Z`,
        valid_until: `${formData.valid_until}T23:59:59Z`
      };
      
      console.log('쿠폰 생성 요청 데이터:', requestData);
      
      const response = await createDummyOrderAndIssueGiftCoupons(requestData);

      if (response.success) {
        alert(`성공적으로 ${response.coupon_codes?.length}개의 쿠폰이 생성되었습니다.\n주문 ID: ${response.order_id}`);
        navigate('/admin/coupon');
      } else {
        throw new Error(response.message || '쿠폰 생성에 실패했습니다.');
      }
      
    } catch (err) {
      console.error('쿠폰 생성 실패:', err);
      setError(err instanceof Error ? err.message : '쿠폰 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 권한 확인
  if (!hasAdminPermission() && !hasSystemPermission()) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>접근 권한이 없습니다</h2>
          <p>이 페이지에 접근하려면 관리자 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button 
            className={styles.btnSecondary}
            onClick={() => navigate('/admin/coupon')}
          >
            ← 목록으로
          </button>
          <h1 className="admin-page-title">선물용 쿠폰 생성</h1>
        </div>
      </div>

      <div className={styles.content}>
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* 에러 메시지 */}
          {error && (
            <div className={styles.errorContainer}>
              <p>{error}</p>
            </div>
          )}

          {/* 기본 정보 */}
          <div className={styles.section}>
            <h2>쿠폰 생성 정보</h2>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="product_id">상품 선택 *</label>
                <select
                  id="product_id"
                  name="product_id"
                  value={formData.product_id}
                  onChange={handleInputChange}
                  className={styles.select}
                  required
                >
                  <option value="">상품을 선택하세요</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} - {product.price.toLocaleString()}원
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="count">쿠폰 수량 *</label>
                <input
                  type="number"
                  id="count"
                  name="count"
                  value={formData.count}
                  onChange={handleInputChange}
                  placeholder="1"
                  className={styles.input}
                  min="1"
                  max="100"
                  required
                />
                <small className={styles.inputHint}>생성할 쿠폰의 개수 (1-100개)</small>
              </div>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="valid_from">발행일 (쿠폰 사용 시작일) *</label>
                <input
                  type="date"
                  id="valid_from"
                  name="valid_from"
                  value={formData.valid_from}
                  onChange={handleInputChange}
                  className={styles.input}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
                <small className={styles.inputHint}>쿠폰을 사용할 수 있는 시작일</small>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="valid_until">만료일 (쿠폰 사용 마지막 날짜) *</label>
                <input
                  type="date"
                  id="valid_until"
                  name="valid_until"
                  value={formData.valid_until}
                  onChange={handleInputChange}
                  className={styles.input}
                  min={formData.valid_from || new Date().toISOString().split('T')[0]}
                  required
                />
                <small className={styles.inputHint}>쿠폰 사용 가능한 마지막 날짜</small>
              </div>
            </div>
          </div>

          {/* 선택된 상품 정보 */}
          {selectedProduct && (
            <div className={styles.section}>
              <h2>선택된 상품 정보</h2>
              <div className={styles.productInfo}>
                <div className={styles.productCard}>
                  <h3>{selectedProduct.name}</h3>
                  <p className={styles.productPrice}>{selectedProduct.price.toLocaleString()}원</p>
                  {selectedProduct.description && (
                    <p className={styles.productDescription}>{selectedProduct.description}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 생성 예상 정보 */}
          {selectedProduct && formData.count > 0 && (
            <div className={styles.section}>
              <h2>생성 예상 정보</h2>
              <div className={styles.summaryInfo}>
                <div className={styles.summaryItem}>
                  <label>생성될 쿠폰 개수</label>
                  <div className={styles.summaryValue}>{formData.count}개</div>
                </div>
                <div className={styles.summaryItem}>
                  <label>쿠폰 할인 금액</label>
                  <div className={styles.summaryValue}>{selectedProduct.price.toLocaleString()}원</div>
                </div>
                <div className={styles.summaryItem}>
                  <label>총 할인 금액</label>
                  <div className={styles.summaryValue}>
                    {(selectedProduct.price * formData.count).toLocaleString()}원
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 버튼 */}
          <div className={styles.buttonContainer}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => navigate('/admin/coupon')}
            >
              취소
            </button>
            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={loading || !formData.product_id || formData.count <= 0 || !formData.valid_from || !formData.valid_until}
            >
              {loading ? '생성 중...' : '쿠폰 생성'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CouponCreate;
